import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowCircleLeft as ArrowCircleLeftIcon, Close as CloseIcon } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Autocomplete,
  Box,
  IconButton,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { QueryClient, useMutation } from '@tanstack/react-query';
import { serialize } from 'object-to-formdata';
import { flushSync } from 'react-dom';
import { Controller, useForm } from 'react-hook-form';

import { GenericDialog } from '@/components/NewUI/Components';
import { Button, Snackbar } from '@/components/NewUI/ComponentsLibrary';
import useEditConflict from '@/components/NewUI/hooks/useEditConflict';
import { RootQueryProvider, useUnsavedChangesGuard } from '@/components/NewUI/SingleRoot';
import { formatDate } from '@/components/NewUI/utils/dateTime';

import {
  buildAnswersAttributes,
  buildApprovalPayload,
  buildApproversAttributes,
  buildDefaultApprovers,
  buildDocumentsAttributes,
  buildExistingApprovers,
  containsDuplicates,
  createApprovalFormQuestionsObject,
  createQuestionErrorsObject,
  hydrateQuestionAnswers
} from './newApprovalMappers';
import {
  createNewApprovalSetupSchema,
  getNewApprovalSetupDefaults,
  hasNewApprovalUnsavedChanges
} from './newApprovalSetupSchema';
import { classes } from './styles';
import { LoadingButton } from '../../Components/Buttons/LoadingButton';
import {
  DebouncedFormTextField,
  FormTextField
} from '../../Components/CustomUIElements/FormTextField';
import { sharedClasses } from '../../Components/CustomUIElements/sharedClasses';
import AttachmentSelector from '../../Components/Utilities/AttachmentSelector';
import DocTypeIcon from '../../Components/Utilities/DocTypeIcon';
import { emailIsValid } from '../../Components/Utilities/emailIsValid';
import { formatUserPermissions } from '../../Components/Utilities/formatUserPermissions';
import { humanFileSize } from '../../Components/Utilities/humanFileSize';
import QuestionFieldGenerator from '../../Components/Utilities/QuestionFieldGenerator';
import { scrollToElement } from '../../utils/scroll-to-element';
import Api from '../API';
import { APPROVAL_NOTE_MAX_LENGTH, APPROVAL_POSITION_TITLE_MAX_LENGTH } from '../config';

import type { FileWithPath } from 'react-dropzone';

import type { NewApprovalSetupValues } from './newApprovalSetupSchema';
import type { IApplicantQuestion } from '../../Components/Utilities/QuestionFieldGenerator';
import type { IJob } from '../../Jobs/types';
import type {
  IApprovalForm,
  IApprovalManagers,
  IApprovalTemplate,
  IApprovers,
  IAssignableUsers,
  IAttachedDocument,
  ITabs
} from '../types';

// Other forms import these helpers from here; the implementations live in ./newApprovalMappers.
export {
  containsDuplicates,
  createQuestionErrorsObject,
  normalizeAvailabilityAnswerOffsets
} from './newApprovalMappers';

// Read when the component mounts rather than when the module is evaluated, so the values always
// belong to the page the editor is rendered on.
const readApprovalUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);

  return {
    applicationId: urlParams.get('application_id'), // new approval created from the candidate application
    cognologyRecordId: urlParams.get('cognology_record_id'), // new approval created from the application export cognology
    jobId: urlParams.get('job_id'), // new approval created from the candidate application
    newUI: urlParams.get('new_ui'), // new approval created from the NEW UI application export cognology
    templateIdFromParams: urlParams.get('form_id') // new approval created from the approval forms page
  };
};
const BASE_URL = window.location.origin;
const DEFAULT_DOCUMENTS_SIZE_LIMIT_BYTES = 18 * 1024 * 1024;
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
});

const getApproverOptionLabel = (
  option: { name?: string; email?: string } | null,
  formState: string
): string => {
  if (!option) return '';
  if (option.name?.includes('deleted')) {
    return formState === 'draft' ? 'Please select an approval manager' : (option.name ?? '');
  }
  return option.name ? `${option.name} (${option.email})` : `${option.email}`;
};

const ApprovalTabPanel = ({ children, tabValue, index, ...other }: ITabs) => (
  <div
    aria-labelledby={`approvals-tab-${index}`}
    hidden={tabValue !== index}
    id={`approvals-tabpanel-${index}`}
    role="tabpanel"
    {...other}
  >
    {tabValue === index && <>{children}</>}
  </div>
);

const ApprovalEditor = ({
  entityApprovalAdmin,
  requisitionId, // used for editing new approval
  templateId // used for editing new approval
}: {
  entityApprovalAdmin: string;
  requisitionId?: number;
  templateId?: number;
}): React.JSX.Element => {
  const { applicationId, cognologyRecordId, jobId, newUI, templateIdFromParams } = useMemo(
    () => readApprovalUrlParams(),
    []
  );
  const [approvalTemplateData, setApprovalTemplateData] = useState<IApprovalTemplate | null>(null);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [loadingApprovers, setLoadingApprovers] = useState<boolean>(false);
  const [loadingEmailTemplates, setLoadingEmailTemplates] = useState<boolean>(false);
  const [loadingAssignableUsers, setLoadingAssignableUsers] = useState<boolean>(false);
  const [indicatorWidth, setIndicatorWidth] = useState(0);
  const [approvers, setApprovers] = useState<
    {
      email: string;
      name?: string;
      id?: number;
      destroyed?: boolean;
      error?: string;
      mandatoryBlank?: boolean;
    }[]
  >([]);
  const [approvalManagers, setApprovalManagers] = useState<IApprovalManagers[]>([]);
  const [snackbar, setSnackbar] = useState<{
    message: string;
    state: 'success' | 'warning' | 'error';
  }>({
    message: '',
    state: 'success'
  });
  const [emailTemplates, setEmailTemplates] = useState<[string, { id: number; name: string }[]][]>(
    []
  );
  const [fileList, setFileList] = useState<FileWithPath[]>([]);
  const [attachedDocuments, setAttachedDocuments] = useState<IAttachedDocument[]>([]);
  const [questions, setQuestions] = useState<IApplicantQuestion[]>([]);
  const [approvalFormQuestions, setApprovalFormQuestions] = useState(() =>
    createApprovalFormQuestionsObject([])
  );
  const [questionErrors, setQuestionErrors] = useState(() =>
    createQuestionErrorsObject(questions ?? [])
  );

  const positionTitleRef = useRef<HTMLDivElement>(null);
  const approvalManagersRef = useRef<HTMLDivElement>(null);
  const questionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [assignableUsers, setAssignableUsers] = useState<IAssignableUsers[]>([]);
  const [fetchingCSV, setFetchingCSV] = useState<boolean>(false);
  const [approvalFormData, setApprovalFormData] = useState<IApprovalForm>({});
  const [editVersion, setEditVersion] = useState<number | undefined>(undefined);
  const editConflict = useEditConflict({
    recordLabel: 'approval',
    inApproval: Boolean(approvalFormData.state && approvalFormData.state !== 'draft')
  });
  const updateMutation = useMutation({
    mutationFn: async (body: FormData) =>
      fetch(`${BASE_URL}/api/v4/requisitions/${requisitionId}`, { method: 'PUT', body }).then(
        async (res) => res.json()
      ),
    meta: { skipGlobalErrorSnackbar: true }
  });
  const [loadingFormData, setLoadingFormData] = useState<boolean>(false);
  const [approvalFormDataSettled, setApprovalFormDataSettled] = useState<boolean>(false);
  const [assignableUsersSettled, setAssignableUsersSettled] = useState<boolean>(false);
  const [dataInitialized, setDataInitialized] = useState<boolean>(false);
  const [jobs, setJobs] = useState<IJob[]>([]);
  const [job, setJob] = useState<number | null>(null);
  const [applications, setApplications] = useState<{ id: number; candidate_name: string }[]>([]);
  const [fetchingJobs, setFetchingJobs] = useState<boolean>(false);
  const [fetchingApplications, setFetchingApplications] = useState<boolean>(false);
  const [fetchingInitialJobsAndApplications, setFetchingInitialJobsAndApplications] =
    useState<boolean>(false);
  const [approvalAnsweredIds, setApprovalAnsweredIds] = useState<number[] | null>(null);
  const [exitConfirmation, setExitConfirmation] = useState<boolean>(false);
  const [isLeaving, setIsLeaving] = useState<boolean>(false);
  const [isEdited, setIsEdited] = useState<boolean>(false);

  // Derived from the template on render rather than pushed into state by the initialisation
  // effect below, which resets the whole setup form and must run once per loaded approval.
  const administratorList = useMemo<IApprovers[]>(() => {
    if (!approvalTemplateData) return [];

    return entityApprovalAdmin
      ? [
          { label: `Entity Default (${entityApprovalAdmin})`, value: entityApprovalAdmin },
          ...approvalTemplateData.approvers
        ]
      : [...approvalTemplateData.approvers];
  }, [approvalTemplateData, entityApprovalAdmin]);

  // Every setup-form request must land before initialising: the template (all modes), plus when
  // editing, the approval and its assignable users. The settled flags cover failed requests too,
  // so a failed fetch still leaves a usable form rather than a skeleton.
  const isEditingExistingApproval = !templateIdFromParams;
  const isSetupDataReady =
    !!approvalTemplateData &&
    (!isEditingExistingApproval || (approvalFormDataSettled && assignableUsersSettled));
  const setupInitializationKey = isSetupDataReady
    ? `${approvalTemplateData.id}:${approvalFormData.id ?? 'new'}`
    : null;
  const setupInitializedKeyRef = useRef<string | null>(null);
  // State mirror of setupInitializedKeyRef — effects that must re-run once setup has actually
  // finished initializing (e.g. reconciling approvalEmailTemplate below) depend on it; a ref alone
  // can't re-trigger them, since setting a ref doesn't cause a re-render.
  const [setupInitializedKey, setSetupInitializedKey] = useState<string | null>(null);

  const showPositionTitle = !Number(approvalTemplateData?.config?.hide_position_title);
  const setupSchema = useMemo(
    () => createNewApprovalSetupSchema(showPositionTitle),
    [showPositionTitle]
  );
  const {
    control,
    formState: { errors: setupErrors, isDirty: setupIsDirty },
    getValues: getSetupValues,
    reset: resetSetup,
    setValue: setSetupValue,
    trigger: validateSetup
  } = useForm<NewApprovalSetupValues>({
    defaultValues: getNewApprovalSetupDefaults(entityApprovalAdmin),
    resolver: zodResolver(setupSchema)
  });
  const hasUnsavedChanges = hasNewApprovalUnsavedChanges(isEdited, setupIsDirty);
  const userPermissions = formatUserPermissions();

  const documentsSizeLimitBytes =
    approvalFormData.documents_size_limit_bytes ?? DEFAULT_DOCUMENTS_SIZE_LIMIT_BYTES;

  const totalExistingDocumentsSize = useMemo(
    () => attachedDocuments.reduce((sum, document) => sum + (document.attached_file_size ?? 0), 0),
    [attachedDocuments]
  );

  const totalNewFilesSize = useMemo(
    () => fileList.reduce((sum, file) => sum + (file.size ?? 0), 0),
    [fileList]
  );

  const totalDocumentsSize = totalExistingDocumentsSize + totalNewFilesSize;
  const remainingBytes = documentsSizeLimitBytes - totalDocumentsSize;
  const sizeStatusText =
    remainingBytes >= 0
      ? `${humanFileSize(remainingBytes, false)} remaining`
      : `${humanFileSize(Math.abs(remainingBytes), false)} over the limit`;
  const isOverDocumentsLimit = remainingBytes < 0;

  const validateQuestions = () => {
    let isValid = true;
    let questionError: number | undefined;

    questions
      .filter((question) => (requisitionId ? approvalAnsweredIds?.includes(question.id) : true))
      .forEach((question: IApplicantQuestion, index: number) => {
        let tempErrorMessage = '';
        if (question.type === 'CheckBoxField') {
          const isChecked = Object.values(approvalFormQuestions[question.id].data).some(
            (value) => value === true
          );
          if (question.required && !isChecked) {
            tempErrorMessage = 'Must select at least one option';
            questionError ??= index;
            isValid = false;
          }
        } else if (question.type === 'RankField') {
          const duplicates = containsDuplicates(
            Object.values(approvalFormQuestions[question.id].data)
          );
          if (duplicates) {
            tempErrorMessage = 'Ranked choices must be unique';
            questionError ??= index;
            isValid = false;
          }
        } else if (question.required && !approvalFormQuestions[question.id].data) {
          tempErrorMessage =
            question.type !== 'RadioButtonField'
              ? 'This field is required'
              : 'Must select an option';
          questionError ??= index;
          isValid = false;
        }
        setQuestionErrors((prev) => ({
          ...prev,
          [question.id]: tempErrorMessage
        }));
      });

    if (questionError !== undefined) {
      scrollToElement(questionRefs.current[questionError]);
    }

    return isValid;
  };

  const validateSetupFields = async () => {
    const isValid = await validateSetup();
    if (!isValid) {
      scrollToElement(positionTitleRef);
    }
    return isValid;
  };

  const validateApprovalManagers = () => {
    let isValid = true;
    const newApprovers = approvers.map((approver) => {
      let error = '';
      if (!approver.email) {
        error = 'Please choose an approver or delete the row.';
        isValid = false;
      }
      if (!emailIsValid(approver.email)) {
        error = 'Please enter a valid email address.';
        isValid = false;
      }
      return { ...approver, error };
    });
    if (!isValid) {
      scrollToElement(approvalManagersRef);
    }
    setApprovers(newApprovers);
    return isValid;
  };

  const getApprovalData = () =>
    buildApprovalPayload({
      answersAttributes: buildAnswersAttributes(
        approvalFormQuestions,
        approvalFormData.answers,
        approvalAnsweredIds,
        !!requisitionId
      ),
      approversAttributes: buildApproversAttributes(approvers),
      cognologyRecordId,
      documentsAttributes: buildDocumentsAttributes(fileList),
      newUI,
      setupValues: getSetupValues(),
      templateId,
      templateIdFromParams
    });

  const handleSaveDraft = async () => {
    const formData = serialize({ ...getApprovalData(), draft: true });
    setIsEdited(false);
    resetSetup(getSetupValues());

    try {
      const response = await fetch(`${BASE_URL}/api/v4/requisitions`, {
        method: 'POST',
        body: formData
      }).then(async (res) => res.json());
      if (response.success === 'Draft created') {
        window.location.href = response.url;
        setSnackbar({
          message: 'Approval form was saved successfully',
          state: 'success'
        });
      } else {
        throw response;
      }
    } catch (error) {
      setSnackbar({
        message: error.error ?? `Something went wrong. Please try again. ${error}`,
        state: 'error'
      });
    }
  };

  const handleSubmitApproval = async () => {
    if (!(await validateSetupFields()) || !validateQuestions() || !validateApprovalManagers())
      return;
    const formData = serialize(getApprovalData());
    setIsEdited(false);
    resetSetup(getSetupValues());

    try {
      const response = await fetch(`${BASE_URL}/api/v4/requisitions`, {
        method: 'POST',
        body: formData
      }).then(async (res) => res.json());
      if (response.success === 'Approval created') {
        window.location.href = response.url;
        setSnackbar({
          message: 'Approval form created successfully',
          state: 'success'
        });
      } else {
        throw response;
      }
    } catch (error) {
      setSnackbar({
        message: error.error ?? `Something went wrong. Please try again. ${error}`,
        state: 'error'
      });
    }
  };

  const updateApprovalForm = async (state?: string, force = false) => {
    if (
      !state &&
      (!(await validateSetupFields()) || !validateQuestions() || !validateApprovalManagers())
    )
      return;
    const formData = state
      ? serialize({ ...getApprovalData(), draft: true })
      : serialize(getApprovalData());
    if (editConflict.enabled && editVersion !== undefined) {
      formData.append('edit_version', String(editVersion));
    }
    if (editConflict.enabled) formData.append('force', String(force));

    try {
      const response = await updateMutation.mutateAsync(formData);
      if (editConflict.handleConflict(response, async () => updateApprovalForm(state, true)))
        return;
      if (response.success === 'Approval updated') {
        flushSync(() => {
          setEditVersion((previous) => response.edit_version ?? previous);
          setIsEdited(false);
          resetSetup(getSetupValues());
        });
        window.location.href = response.url;
        setSnackbar({
          message: 'Approval form updated successfully',
          state: 'success'
        });
      } else {
        throw response;
      }
    } catch (error) {
      if (editConflict.handleConflict(error, async () => updateApprovalForm(state, true))) return;
      setSnackbar({
        message: error.errors ?? `Something went wrong. Please try again. ${error}`,
        state: 'error'
      });
    }
  };

  const getApprovalTemplateData = useCallback(async () => {
    setLoadingData(true);
    const parsedTemplateId = Number(templateIdFromParams);
    const approvalTemplateId =
      Number.isNaN(parsedTemplateId) || parsedTemplateId === 0 ? templateId : parsedTemplateId;
    if (!approvalTemplateId) return;
    try {
      const data = await Api.getApprovalTemplate(approvalTemplateId);
      setApprovalTemplateData(data.res);
    } catch (err) {
      setSnackbar({
        message: `Failed to fetch approval template data, ${err}`,
        state: 'error'
      });
    } finally {
      setLoadingData(false);
    }
  }, [templateId, templateIdFromParams]);

  const handleLookUpButton = async (questionId: number, positionId: number) => {
    setFetchingCSV(true);
    setIsEdited(true);
    try {
      const data = await Api.getCSVData(questionId, positionId);
      setApprovers(
        data.res.approvers.map((approver: string) => ({
          email: approver.toLowerCase(),
          destroyed: false
        }))
      );
      questions?.forEach((question) => {
        const answerExist = data.res.fields[0][question.original_title];
        if (answerExist) {
          setApprovalFormQuestions((prev) => ({
            ...prev,
            [question.id]: {
              ...prev[question.id],
              data:
                question.type === 'SelectField' || question.type === 'RadioButtonField'
                  ? question.field_choices.find((e) => e.name === answerExist)?.id
                  : answerExist
            }
          }));
        }
      });
    } catch (err) {
      setSnackbar({
        message: `Failed to fetch csv data, ${err}`,
        state: 'error'
      });
    } finally {
      setFetchingCSV(false);
    }
  };

  async function getEmailTemplates() {
    setLoadingEmailTemplates(true);
    try {
      const data = await Api.getEmailTemplates();
      setEmailTemplates(data.res.email_templates);
    } catch (err) {
      setSnackbar({
        message: `Failed to fetch email templates, ${err}`,
        state: 'error'
      });
    } finally {
      setLoadingEmailTemplates(false);
    }
  }

  async function getApproverManagers() {
    setLoadingApprovers(true);
    try {
      const data = await Api.getApprovalManagers({
        override_limit: true,
        limit: 2000,
        sort: 'name',
        sort_order: 'asc'
      });
      setApprovalManagers(data.res.requisition_managers);
    } catch (err) {
      setSnackbar({
        message: `Failed to fetch approval managers, ${err}`,
        state: 'error'
      });
    } finally {
      setLoadingApprovers(false);
    }
  }

  async function getAssignableUsers() {
    setLoadingAssignableUsers(true);
    try {
      const data = await Api.getAssignableUsers();
      setAssignableUsers(data.res.assignable_users);
    } catch (err) {
      setSnackbar({
        message: `Failed to fetch assignable users, ${err}`,
        state: 'error'
      });
    } finally {
      setLoadingAssignableUsers(false);
      setAssignableUsersSettled(true);
    }
  }

  const getApprovalFormData = useCallback(async () => {
    if (!requisitionId) return;
    setLoadingFormData(true);
    try {
      const data = await Api.getApprovalForm(requisitionId);
      setApprovalFormData(data.res);
      setEditVersion(data.res.edit_version);

      // Only previous answered questions will be displayed, newly added questions will not show in edit page
      const ids = data.res.answers.map((answer) => parseInt(answer.field_id, 10));
      setApprovalAnsweredIds(ids);
    } catch (err) {
      setSnackbar({
        message: `Failed to fetch approval form data, ${err}`,
        state: 'error'
      });
    } finally {
      setLoadingFormData(false);
      setApprovalFormDataSettled(true);
    }
  }, [requisitionId]);

  const headerRefs = useRef(null);

  const TabProps = useCallback(
    (index: number) => ({
      id: `approvals-tab-${index}`,
      'aria-controls': `approvals-tabpanel-${index}`
    }),
    []
  );

  async function getJobs() {
    setFetchingJobs(true);
    try {
      const data = await Api.getJobs();
      setJobs(data.res.jobs);
    } catch (err) {
      setSnackbar({
        message: `Failed fetching job options. Please try again. ${err}`,
        state: 'error'
      });
    } finally {
      setFetchingJobs(false);
    }
  }

  async function getApplications(selectedJobId: number) {
    setFetchingApplications(true);
    try {
      const data = await Api.getApplications(selectedJobId);
      setApplications(data.res);
    } catch (err) {
      setSnackbar({
        message: `Failed fetching application options. Please try again. ${err}`,
        state: 'error'
      });
    } finally {
      setFetchingApplications(false);
    }
  }

  useEffect(() => {
    if (headerRefs.current) {
      setIndicatorWidth(headerRefs.current.clientWidth - 32);
    }
    getApprovalTemplateData();
  }, [getApprovalTemplateData]);

  useEffect(() => {
    queueMicrotask(() => {
      getApprovalFormData();
    });
  }, [getApprovalFormData]);

  useEffect(() => {
    queueMicrotask(() => {
      if (!isSetupDataReady) {
        setDataInitialized(false);
        return;
      }

      // `resetSetup` discards whatever is in the form, so it must run exactly once per loaded
      // approval. The effect also depends on data from separate requests (assignable users, the
      // approval itself), and without this guard a late response re-ran the reset and wiped
      // fields the user had already typed - most visibly the vacancy position title.
      if (setupInitializedKeyRef.current === setupInitializationKey) return;
      setupInitializedKeyRef.current = setupInitializationKey;
      setSetupInitializedKey(setupInitializationKey);
      setDataInitialized(false);

      if (templateIdFromParams) {
        // Only enabled questions for New approval form
        const questionsData = approvalTemplateData.fields.filter(
          (question) => question.enabled && !question.for_email_template
        );
        setQuestions(questionsData);
        setApprovalFormQuestions(createApprovalFormQuestionsObject(questionsData));

        // If no default approvers set in approval template but has default approvers in entity level
        const defaultApprovers = buildDefaultApprovers(approvalTemplateData);
        if (defaultApprovers) setApprovers(defaultApprovers);
        resetSetup({
          ...getNewApprovalSetupDefaults(entityApprovalAdmin),
          application: applicationId ? Number(applicationId) : null,
          approvalEmailTemplate: approvalTemplateData.email_template_id
        });
        if (jobId && applicationId) {
          setFetchingInitialJobsAndApplications(true);
          getApplications(Number(jobId));
          setJob(Number(jobId));
        }
      } else {
        // All questions for EDIT approval form which includes answered but disabled questions
        const questionsData = approvalTemplateData.fields.filter(
          (question) => !question.for_email_template
        );
        setQuestions(questionsData);

        const approvalAdministratorValue = administratorList.some(
          (approver) => approver.value === approvalFormData.approval_administrator
        )
          ? approvalFormData.approval_administrator
          : '';
        setApprovers(buildExistingApprovers(approvalFormData, approvalTemplateData));
        const formAssignedUser = assignableUsers.find(
          (user) => `${user.firstname} ${user.lastname}` === approvalFormData.assigned_user
        );
        resetSetup({
          application: approvalFormData.application?.id ?? null,
          approvalAdministrator: approvalAdministratorValue,
          approvalEmailTemplate: approvalFormData.email_template?.id ?? null,
          assignedUser: formAssignedUser?.id ?? null,
          notes: approvalFormData.note ?? '',
          positionTitle: showPositionTitle ? (approvalFormData.position_title ?? '') : ''
        });
        setAttachedDocuments(approvalFormData.documents ?? []);
        setApprovalFormQuestions(
          hydrateQuestionAnswers(
            createApprovalFormQuestionsObject(questionsData),
            approvalFormData.answers
          )
        );
        if (approvalFormData.application) {
          setFetchingInitialJobsAndApplications(true);
          getApplications(approvalFormData.application.job_id);
          setJob(approvalFormData.application.job_id);
        }
      }
    });
  }, [
    administratorList,
    applicationId,
    approvalFormData,
    approvalTemplateData,
    assignableUsers,
    entityApprovalAdmin,
    isSetupDataReady,
    jobId,
    resetSetup,
    setupInitializationKey,
    showPositionTitle,
    templateIdFromParams
  ]);

  useEffect(() => {
    // A persisted email_template_id can predate the approval-viable filter (or point at a template
    // later edited/disabled) and so be absent from the fetched, filtered options. The Autocomplete
    // renders that as blank, but the stale id stays in the form and is silently resubmitted on
    // every save (UNS-7084). Keyed on setupInitializedKey (state, not the ref) so it re-checks
    // once setup has actually finished initializing, however the fetches interleave.
    if (loadingEmailTemplates || setupInitializedKey !== setupInitializationKey) return;
    if (!setupInitializationKey) return;

    const currentEmailTemplate = getSetupValues('approvalEmailTemplate');
    if (currentEmailTemplate == null) return;

    const stillValid = emailTemplates.some(([, entityTemplates]) =>
      entityTemplates.some((template) => template.id === currentEmailTemplate)
    );
    if (!stillValid) {
      setSetupValue('approvalEmailTemplate', null);
    }
  }, [
    emailTemplates,
    getSetupValues,
    loadingEmailTemplates,
    setSetupValue,
    setupInitializationKey,
    setupInitializedKey
  ]);

  useEffect(() => {
    queueMicrotask(() => {
      setDataInitialized(true);
    });
  }, [approvalFormQuestions]);

  useEffect(() => {
    if (applications.length > 0) {
      queueMicrotask(() => {
        setFetchingInitialJobsAndApplications(false);
      });
    }
  }, [applications]);

  useEffect(() => {
    queueMicrotask(() => {
      getApproverManagers();
      getEmailTemplates();
      getAssignableUsers();
      getJobs();
    });
  }, []);

  const handleAddApprover = () => {
    setIsEdited(true);
    setApprovers([...approvers, { email: '', destroyed: false }]);
  };

  const removeApprover = (i: number) => {
    setIsEdited(true);
    if (approvers.length > 1) {
      const newApprovers = [...approvers];
      if (newApprovers[i].id) {
        newApprovers[i].destroyed = true;
      } else {
        newApprovers.splice(i, 1);
      }
      setApprovers(newApprovers);
    }
  };

  const handleEmailChange = (i: number, value: IApprovalManagers) => {
    setIsEdited(true);
    const newApprovers = [...approvers];
    newApprovers[i].email = value ? value.email : '';
    setApprovers(newApprovers);
  };

  const handleDeleteAttachment = (index: number) => {
    setIsEdited(true);
    const updatedFileList = fileList.filter((file, i) => i !== index);

    setFileList(updatedFileList);
  };

  const templates = emailTemplates.reduce<{ id: number; name: string; entity: string }[]>(
    (acc, currVal) => {
      const entity: string = currVal[0];
      const arrayOfTemplates: { id: number; name: string }[] = currVal[1];
      return [...acc, ...arrayOfTemplates.map((template) => ({ ...template, entity }))];
    },
    []
  );

  const handleDeleteDocument = async (i: number, id: number) => {
    try {
      await Api.putApprovalForm(requisitionId, {
        requisition: { documents_attributes: { [i]: { id, _destroy: true } } }
      });
    } catch (err: unknown) {
      const errorMessage =
        (err as { errors?: string })?.errors ?? (err as Error)?.message ?? 'Unknown error';
      setSnackbar({
        message: `Failed to delete document, ${errorMessage}`,
        state: 'error'
      });
    } finally {
      const newAttachedDocuments = [...attachedDocuments];
      newAttachedDocuments.splice(i, 1);
      setAttachedDocuments(newAttachedDocuments);
    }
  };

  const handleJobChange = (id: number) => {
    setIsEdited(true);
    if (id === job) return;
    setApplications([]);
    setJob(id);
    getApplications(id);
    setSetupValue('application', null, { shouldDirty: true });
  };

  const handleDeleteJob = () => {
    setIsEdited(true);
    setJob(null);
    setSetupValue('application', null, { shouldDirty: true });
    setApplications([]);
  };

  const handleEmailInput = (i: number, value: string) => {
    setIsEdited(true);
    const newApprovers = [...approvers];
    newApprovers[i].email = value;
    setApprovers(newApprovers);
  };

  const leaveGuard = useUnsavedChangesGuard(hasUnsavedChanges && !exitConfirmation);

  return (
    <>
      {editConflict.dialog}
      <Box sx={classes.jobPageContainer}>
        <Box sx={classes.jobPageHeader}>
          {loadingData ? (
            <Skeleton animation="wave" height={56} width={600} />
          ) : (
            <h1>
              {approvalFormData?.position_title
                ? approvalFormData.position_title
                : approvalTemplateData?.name}
            </h1>
          )}
        </Box>
        <Box sx={classes.tabsContainer}>
          <Tabs
            aria-label="Approvals"
            scrollButtons={false}
            value={0}
            variant="scrollable"
            sx={{
              ...classes.tabs,
              '& .MuiTabs-indicator': { ...classes.tabIndicator, maxWidth: `${indicatorWidth}px` }
            }}
          >
            <Tab
              ref={headerRefs}
              label={requisitionId ? 'Edit approval form' : 'New approval form'}
              {...TabProps(0)}
            />
          </Tabs>
        </Box>
        <Box>
          <ApprovalTabPanel index={0} tabValue={0} />
        </Box>
        <Box sx={classes.backToJobButton}>
          <Stack
            onClick={() => {
              if (hasUnsavedChanges) {
                setExitConfirmation(true);
              } else {
                window.location.href = '/admin/requisitions';
              }
            }}
            sx={{
              alignItems: 'center',
              flexDirection: 'row'
            }}
          >
            <IconButton sx={classes.iconGrey}>
              <ArrowCircleLeftIcon />
            </IconButton>
            <Box>Back to approval forms</Box>
          </Stack>
        </Box>
        <Box sx={{ padding: 2 }}>
          {loadingData || !dataInitialized ? (
            <Skeleton animation="wave" height={56} width={600} />
          ) : (
            <>
              {showPositionTitle ? (
                <Box sx={{ paddingBottom: 3 }}>
                  <Controller
                    control={control}
                    name="positionTitle"
                    render={({ field }) => (
                      <FormTextField
                        required
                        error={setupErrors.positionTitle?.message ?? ''}
                        id="position-title-input"
                        innerRef={positionTitleRef}
                        label="Vacancy position title"
                        maxLength={APPROVAL_POSITION_TITLE_MAX_LENGTH}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                        styles={{ width: '300px' }}
                        value={field.value}
                      />
                    )}
                  />
                </Box>
              ) : null}
              {questions?.map(
                (question, index) =>
                  (!approvalAnsweredIds || approvalAnsweredIds.includes(question.id)) && (
                    <Box key={question.id} sx={{ paddingBottom: 3 }}>
                      <QuestionFieldGenerator
                        clearable
                        answer={approvalFormQuestions[question.id]?.data}
                        fetchingCSV={fetchingCSV}
                        handleLookUpButton={handleLookUpButton}
                        question={question}
                        questionError={questionErrors[question.id]}
                        onChangeCallback={(id, value) => {
                          setIsEdited(true);
                          setApprovalFormQuestions((prev) => ({
                            ...prev,
                            [id]: {
                              ...prev[id],
                              data: value
                            }
                          }));
                          setQuestionErrors((prev) => ({
                            ...prev,
                            [id]: value || !question.required ? '' : 'This field is required'
                          }));
                        }}
                        questionRef={(element) => {
                          questionRefs.current[index] = element;
                        }}
                      />
                    </Box>
                  )
              )}
              {/* TODO: Hide this section for now */}
              {/* {emailTemplateQuestions.length > 0 && (
              <Stack sx={{ ...classes.sectionHeader, paddingBottom: 2 }}>
                Email Template Questions
              </Stack>
            )}
            {emailTemplateQuestions?.map((question, index) => (
              <Box sx={{ paddingBottom: 3 }} key={question.id}>
                <QuestionFieldGenerator
                  question={question}
                  questionRef={(element) => {
                    questionRefs.current[index] = element;
                  }}
                  questionError={questionErrors[question.id]}
                  onChangeCallback={(id, value) => {
                    setApprovalFormQuestions((prev) => ({
                      ...prev,
                      [id]: {
                        ...prev[id],
                        data: value
                      }
                    }));
                    setQuestionErrors((prev) => ({
                      ...prev,
                      [id]: value || !question.required ? '' : 'This field is required'
                    }));
                  }}
                  answer={approvalFormQuestions[question.id]?.data}
                  disabled={true}
                />
              </Box>
            ))} */}
            </>
          )}
        </Box>
        {userPermissions?.['Approval Forms']?.[
          `Add/attach documents (complementary to 'Submit Approval Form')`
        ] ? (
          <Box sx={{ padding: 2 }}>
            <Stack sx={{ paddingBottom: 1 }}>
              <Typography sx={{ ...classes.sectionHeader, paddingY: 0 }}>Documents</Typography>
              <Typography sx={classes.sectionSubtitle}>
                {/* Please upload a copy of this roles Position Description below */}
                Please upload required documents below
              </Typography>
            </Stack>
            <AttachmentSelector
              maxSize={50000000}
              performUpload={false}
              setFileList={setFileList}
              setIsEdited={setIsEdited}
            />
            <Typography component="div" sx={classes.fileSummary}>
              Total size: {humanFileSize(totalDocumentsSize, false)} of{' '}
              {humanFileSize(documentsSizeLimitBytes, false)}{' '}
              <Box
                component="span"
                sx={isOverDocumentsLimit ? classes.fileSummaryWarning : classes.fileSummaryDelta}
              >
                ({sizeStatusText})
              </Box>
            </Typography>
            {attachedDocuments
              ? attachedDocuments.map((document, index) => (
                  <Box key={document.id} sx={classes.fileContainer}>
                    <Box sx={classes.fileIconContainer}>
                      <Box sx={classes.fileIcon}>
                        {DocTypeIcon(document?.attached_file_name?.split('.').pop())}
                      </Box>
                      <Box>
                        <Box sx={classes.fileName}>{document.attached_file_name}</Box>
                        <Box sx={classes.fileSize}>
                          {formatDate(document.created_at, 'long')}
                          {document.attached_file_size != null
                            ? ` · ${humanFileSize(document.attached_file_size, false)}`
                            : ''}
                        </Box>
                      </Box>
                    </Box>
                    <IconButton onClick={async () => handleDeleteDocument(index, document.id)}>
                      <DeleteIcon sx={{ color: '#D6827D' }} />
                    </IconButton>
                  </Box>
                ))
              : null}
            {fileList
              ? fileList.map(
                  (file, index) =>
                    file.name && (
                      <Box key={`${file.path}-${file.lastModified}`} sx={classes.fileContainer}>
                        <Box sx={classes.fileIconContainer}>
                          <Box sx={classes.fileIcon}>
                            {DocTypeIcon(file?.name?.split('.').pop())}
                          </Box>
                          <Box>
                            <Box sx={classes.fileName}>{file.name}</Box>
                            <Box sx={classes.fileSize}>
                              {formatDate(file.lastModified, 'long')}
                              {typeof file.size === 'number'
                                ? ` · ${humanFileSize(file.size, false)}`
                                : ''}
                            </Box>
                          </Box>
                        </Box>
                        <IconButton onClick={() => handleDeleteAttachment(index)}>
                          <DeleteIcon sx={{ color: '#D6827D' }} />
                        </IconButton>
                      </Box>
                    )
                )
              : null}
          </Box>
        ) : null}
        {(requisitionId
          ? userPermissions?.['Approval Forms']?.['Edit the assigned user of a requisition']
          : userPermissions?.['Approval Forms']?.['Assign User To Requisition']) &&
        !approvalTemplateData?.config.hide_assigned_user ? (
          <>
            {loadingAssignableUsers ? (
              <Skeleton animation="wave" height={56} width={600} />
            ) : (
              <Box sx={{ padding: 2 }}>
                <Stack sx={{ ...classes.sectionHeader, paddingBottom: 2 }}>Assigned User</Stack>
                <Controller
                  control={control}
                  name="assignedUser"
                  render={({ field }) => (
                    <Autocomplete
                      disablePortal
                      onChange={(_, value) => field.onChange(value?.id ?? null)}
                      options={assignableUsers}
                      slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
                      sx={{ width: '300px', paddingTop: 3, ...sharedClasses.formAutocomplete }}
                      value={assignableUsers.find((user) => user.id === field.value) ?? null}
                      getOptionLabel={(option) =>
                        `${option.firstname} ${option.lastname} (${option.email})`
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Assigned user"
                          placeholder="Please select"
                          slotProps={{
                            ...params.slotProps,
                            inputLabel: { shrink: true }
                          }}
                        />
                      )}
                    />
                  )}
                />
              </Box>
            )}
          </>
        ) : null}
        {loadingData ? (
          <Skeleton animation="wave" height={56} width={600} />
        ) : (
          <>
            {!Number(approvalTemplateData?.config.hide_approval_administrator) &&
            administratorList ? (
              <Box sx={{ padding: 2 }}>
                <Stack sx={{ ...classes.sectionHeader, paddingBottom: 2 }}>
                  Approval Administrator
                </Stack>
                <Controller
                  control={control}
                  name="approvalAdministrator"
                  render={({ field }) => (
                    <Autocomplete
                      disablePortal
                      getOptionLabel={(option) => option.label}
                      onChange={(_, value) => field.onChange(value?.value ?? '')}
                      options={administratorList}
                      slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
                      sx={{ width: '300px', paddingTop: 3, ...sharedClasses.formAutocomplete }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Send to"
                          placeholder="Please select"
                          slotProps={{
                            ...params.slotProps,
                            inputLabel: { shrink: true }
                          }}
                        />
                      )}
                      value={
                        administratorList.find((manager) => manager.value === field.value) ?? null
                      }
                    />
                  )}
                />
              </Box>
            ) : null}
          </>
        )}
        {userPermissions?.['Approval Forms']?.[
          `Add approvers (complementary to 'Submit Approval Form')`
        ] ? (
          <>
            {loadingApprovers ? (
              <Skeleton animation="wave" height={56} width={600} />
            ) : (
              <Box sx={{ padding: 2 }}>
                <Stack ref={approvalManagersRef} sx={classes.sectionHeader}>
                  Approvers
                </Stack>
                {approvers.map((approver, index) => {
                  const deleted = approver?.name?.includes('deleted');
                  const mandatory = approver.email.includes('*');
                  const { mandatoryBlank } = approver;
                  let value = deleted
                    ? approver
                    : approvalManagers.find(
                        (manager) =>
                          (mandatory ? approver.email.slice(1) : approver.email) === manager.email
                      );
                  value ??= approvers.find(
                    (manager) =>
                      (mandatory ? approver.email.slice(1) : approver.email) === manager.email
                  );
                  if (value == null) {
                    const fallbackEmail = mandatory ? approver.email.slice(1) : approver.email;
                    value = fallbackEmail.length > 0 ? fallbackEmail : undefined;
                  }
                  const approverOptions = [
                    ...(approvalTemplateData?.entity_default_approver ?? []),
                    ...approvalManagers
                  ];
                  return (
                    <Box
                      // New unsaved approvers do not have a stable identifier yet.
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                    >
                      <>
                        {!approver.destroyed && (
                          <Box sx={classes.approversContainer}>
                            {approverOptions.length === 0 ? (
                              <FormTextField
                                draggable={mandatory}
                                error={approver.error ? 'Please enter a proper email address' : ''}
                                label="Email"
                                styles={{ width: '290px' }}
                                value={mandatory ? approver.email.slice(1) : approver.email}
                                onChange={(e) => {
                                  handleEmailInput(index, e.target.value);
                                }}
                              />
                            ) : (
                              <Autocomplete
                                disablePortal
                                disabled={!mandatoryBlank && [mandatory, deleted].some(Boolean)}
                                options={approverOptions}
                                sx={{ width: '450px', ...sharedClasses.formAutocomplete }}
                                value={value ?? null}
                                getOptionLabel={(option) =>
                                  getApproverOptionLabel(option, approvalFormData.state)
                                }
                                onChange={(event, selectedApprover) =>
                                  handleEmailChange(index, selectedApprover)
                                }
                                renderInput={(params) => (
                                  <>
                                    <TextField
                                      {...params}
                                      label="Email"
                                      placeholder="Please select"
                                      slotProps={{
                                        ...params.slotProps,
                                        inputLabel: { shrink: true }
                                      }}
                                    />
                                    {approver.error ? (
                                      <Stack
                                        sx={{ color: '#E37D7A', fontSize: '13px', paddingLeft: 1 }}
                                      >
                                        {approver.error}
                                      </Stack>
                                    ) : null}
                                  </>
                                )}
                                renderOption={(
                                  props: React.HTMLAttributes<HTMLLIElement>,
                                  option: INewApprovalOptions
                                ) => (
                                  <Box {...props} key={option.id}>
                                    {option.email}
                                  </Box>
                                )}
                                slotProps={{
                                  listbox: { style: sharedClasses.autoCompleteListStyles }
                                }}
                              />
                            )}
                            {!mandatoryBlank && !mandatory && !deleted && (
                              <Button
                                buttonColor="red"
                                buttonVariant="text"
                                icon={<CloseIcon />}
                                onClick={() => removeApprover(index)}
                              />
                            )}
                          </Box>
                        )}
                      </>
                    </Box>
                  );
                })}
                <Box sx={{ paddingTop: 1 }}>
                  <Button buttonVariant="secondary" onClick={handleAddApprover} size="small">
                    Add approver
                  </Button>
                </Box>
              </Box>
            )}
          </>
        ) : null}
        {userPermissions?.['Approval Forms']?.['Manage Approval Form Notes'] ? (
          <Box sx={{ padding: 2 }}>
            <Stack sx={{ ...classes.sectionHeader, paddingBottom: 2 }}>Notes</Stack>
            <Controller
              control={control}
              name="notes"
              render={({ field }) => (
                <DebouncedFormTextField
                  defaultValue={field.value}
                  label="Add Note"
                  maxLength={APPROVAL_NOTE_MAX_LENGTH}
                  onDebouncedChange={field.onChange}
                  styles={{ width: '300px' }}
                />
              )}
            />
          </Box>
        ) : null}
        {loadingEmailTemplates || loadingData || loadingFormData ? (
          <Skeleton animation="wave" height={56} width={600} />
        ) : (
          <>
            {!approvalTemplateData?.config.hide_email_template && (
              <Box sx={{ padding: 2 }}>
                <Stack sx={{ ...classes.sectionHeader, paddingBottom: 2 }}>Email template</Stack>
                <Controller
                  control={control}
                  name="approvalEmailTemplate"
                  render={({ field }) => (
                    <Autocomplete
                      disablePortal
                      getOptionLabel={(option) => option.name}
                      groupBy={(option) => option.entity}
                      onChange={(_, value) => field.onChange(value?.id ?? null)}
                      options={templates}
                      slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
                      sx={{ width: '300px', paddingTop: 3, ...sharedClasses.formAutocomplete }}
                      value={templates.find((template) => template.id === field.value) ?? null}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select email template"
                          placeholder="(Use default template)"
                          sx={{ input: { '&::placeholder': { opacity: 1 } } }}
                          slotProps={{
                            ...params.slotProps,
                            inputLabel: { shrink: true }
                          }}
                        />
                      )}
                    />
                  )}
                />
              </Box>
            )}
          </>
        )}
        {fetchingInitialJobsAndApplications ? (
          <Skeleton animation="wave" height={56} width={600} />
        ) : (
          <>
            {parseInt(
              approvalTemplateData?.config?.enable_approval_template_for_applications,
              10
            ) === 1 && (
              <Box sx={{ padding: 2 }}>
                <Stack sx={{ ...classes.sectionHeader, paddingBottom: 2 }}>Application</Stack>
                <Autocomplete
                  disablePortal
                  getOptionLabel={(option) => `${option.reference} - ${option.title}`}
                  loading={fetchingJobs}
                  loadingText="Loading jobs..."
                  options={jobs}
                  slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
                  sx={{ width: '300px', paddingTop: 3, ...sharedClasses.formAutocomplete }}
                  value={jobs.find((candidateJob) => candidateJob.id === job) ?? null}
                  onChange={(event, value) =>
                    value ? handleJobChange(value.id) : handleDeleteJob()
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Job"
                      placeholder="Select Job"
                      slotProps={{
                        ...params.slotProps,
                        inputLabel: { shrink: true }
                      }}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="application"
                  render={({ field }) => (
                    <Autocomplete
                      disablePortal
                      getOptionLabel={(option) => `${option.id} - ${option.candidate_name}`}
                      loading={fetchingApplications}
                      loadingText="Loading applications..."
                      onChange={(_, value) => field.onChange(value?.id ?? null)}
                      options={applications}
                      slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
                      sx={{ width: '300px', paddingTop: 5, ...sharedClasses.formAutocomplete }}
                      noOptionsText={
                        job ? 'No applications found for this job' : 'Please choose a job'
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Application"
                          placeholder="Select Application"
                          slotProps={{
                            ...params.slotProps,
                            inputLabel: { shrink: true }
                          }}
                        />
                      )}
                      value={
                        applications.find(
                          (candidateApplication) => candidateApplication.id === field.value
                        ) ?? null
                      }
                    />
                  )}
                />
              </Box>
            )}
          </>
        )}
        <Box sx={{ ...classes.footerButtons, justifyContent: 'space-between' }}>
          <Button
            buttonColor="red"
            buttonVariant="text"
            onClick={() => {
              window.location.href = requisitionId
                ? `/admin/requisitions/${requisitionId}`
                : '/admin/requisitions';
            }}
          >
            Cancel
          </Button>
          <Stack sx={{ flexDirection: 'row', gap: 2 }}>
            {templateIdFromParams || approvalFormData.state === 'draft' ? (
              <LoadingButton
                disabled={updateMutation.isLoading}
                id="save-approval-draft-button"
                type="submit"
                task={async () =>
                  approvalFormData.state === 'draft'
                    ? updateApprovalForm('draft')
                    : handleSaveDraft()
                }
              >
                Save draft
              </LoadingButton>
            ) : null}
            <LoadingButton
              disabled={updateMutation.isLoading}
              id="submit-new-approval"
              type="submit"
              task={async () =>
                templateIdFromParams ? handleSubmitApproval() : updateApprovalForm()
              }
            >
              Submit
            </LoadingButton>
          </Stack>
        </Box>
        <Snackbar
          message={snackbar.message}
          onClose={() => setSnackbar({ message: '', state: 'success' })}
          state={snackbar.state}
        />
        <GenericDialog
          buttonText="Yes, leave"
          callbackLoading={isLeaving}
          description="Are you sure you want to leave the page? Any unsaved changes will be lost."
          isDialogOpen={exitConfirmation}
          secondaryButtonText="Back to editing"
          setDialogOpen={setExitConfirmation}
          title="Leave page?"
          url=""
          buttonCallback={() => {
            setIsLeaving(true);
            window.location.href = '/admin/requisitions';
          }}
        />
        {leaveGuard}
      </Box>
    </>
  );
};

const NewApproval = (props: React.ComponentProps<typeof ApprovalEditor>) => (
  <RootQueryProvider client={queryClient}>
    <ApprovalEditor {...props} />
  </RootQueryProvider>
);

export default NewApproval;
